/** One cell of a custom-section combination, keyed by column id. */
export interface SectionCell {
  columnType?: string;
  value?: unknown;
  columnId?: string;
  columnDetails?: unknown;
}

/** One product/colour/size combination row inside a product. */
export interface SectionCombination {
  columns?: Record<string, SectionCell>;
  [key: string]: unknown;
}

/** One product row of the Range Plan custom-section grid. */
export interface SectionRow {
  _id?: string;
  expandData?: SectionCombination[];
  [key: string]: unknown;
}

/** A custom-section column definition. */
export interface SectionColumn {
  _id?: string;
  columnType?: string;
}

/**
 * Returns the ids of the section's calculation columns — the only cells the server recomputes on
 * read and the only ones a post-save refresh may overwrite.
 *
 * @param {SectionColumn[]} columns - Every column of the section.
 * @returns {string[]} Calculation column ids.
 */
export function getCalculationColumnIds(columns: SectionColumn[]): string[] {
  return (columns || []).filter((column) => column?.columnType === 'calculation' && column?._id).map((column) => String(column._id));
}

/**
 * Works out which 1-based pages of the grid hold the given products. Rows are loaded in pages of
 * `pageSize` in a stable order, so a row's position decides its page.
 *
 * @param {SectionRow[]} rows - The rows currently loaded in the grid, in display order.
 * @param {string[]} devOrderIds - Products whose page is needed.
 * @param {number} pageSize - Rows per page.
 * @returns {number[]} Distinct page numbers, ascending.
 */
export function getPagesForProducts(rows: SectionRow[], devOrderIds: string[], pageSize: number): number[] {
  if (!pageSize || pageSize < 1) {
    return [];
  }
  const wanted = new Set((devOrderIds || []).map(String));
  const pages = new Set<number>();
  (rows || []).forEach((row, index) => {
    if (row?._id && wanted.has(String(row._id))) {
      pages.add(Math.floor(index / pageSize) + 1);
    }
  });
  return [...pages].sort((first, second) => first - second);
}

/**
 * Copies freshly computed calculation cells from `freshRows` into `localRows`, leaving every other
 * cell (including manual edits still in flight) untouched. Products are matched by id and
 * combinations by index — the same key the autosave payload uses. A product whose combination
 * count differs is skipped rather than risk writing a value onto the wrong combination.
 *
 * @param {SectionRow[]} localRows - The grid's current rows.
 * @param {SectionRow[]} freshRows - Rows just re-read from the server.
 * @param {string[]} calculationColumnIds - Ids of the columns to copy.
 * @returns {SectionRow[]} `localRows` itself when nothing changed, otherwise a new array.
 */
export function mergeCalculationColumns(localRows: SectionRow[], freshRows: SectionRow[], calculationColumnIds: string[]): SectionRow[] {
  if (!Array.isArray(localRows) || !Array.isArray(freshRows) || !calculationColumnIds?.length) {
    return localRows;
  }

  const freshById = new Map<string, SectionRow>();
  freshRows.forEach((row) => {
    if (row?._id) {
      freshById.set(String(row._id), row);
    }
  });

  let changed = false;
  const merged = localRows.map((row) => {
    const fresh = row?._id ? freshById.get(String(row._id)) : undefined;
    const mergedRow = fresh ? mergeRowCalculations(row, fresh, calculationColumnIds) : row;
    if (mergedRow !== row) {
      changed = true;
    }
    return mergedRow;
  });

  return changed ? merged : localRows;
}

/**
 * Copies calculation cells from one re-read product into its local row, combination by index.
 *
 * @param {SectionRow} row - The local row.
 * @param {SectionRow} fresh - The same product as just re-read.
 * @param {string[]} calculationColumnIds - Ids of the columns to copy.
 * @returns {SectionRow} `row` itself when nothing changed, otherwise an updated copy.
 */
function mergeRowCalculations(row: SectionRow, fresh: SectionRow, calculationColumnIds: string[]): SectionRow {
  const localCombinations = row.expandData;
  const freshCombinations = fresh.expandData;
  if (!Array.isArray(localCombinations) || !Array.isArray(freshCombinations) || localCombinations.length !== freshCombinations.length) {
    return row;
  }

  let rowChanged = false;
  const expandData = localCombinations.map((combination, index) => {
    const mergedCombination = mergeCombinationCalculations(combination, freshCombinations[index]?.columns, calculationColumnIds);
    if (mergedCombination !== combination) {
      rowChanged = true;
    }
    return mergedCombination;
  });

  return rowChanged ? { ...row, expandData } : row;
}

/**
 * Copies the calculation cells that differ from `freshColumns` into one combination.
 *
 * @param {SectionCombination} combination - The local combination.
 * @param {Record<string, SectionCell> | undefined} freshColumns - The re-read combination's cells.
 * @param {string[]} calculationColumnIds - Ids of the columns to copy.
 * @returns {SectionCombination} `combination` itself when nothing changed, otherwise an updated copy.
 */
function mergeCombinationCalculations(
  combination: SectionCombination,
  freshColumns: Record<string, SectionCell> | undefined,
  calculationColumnIds: string[],
): SectionCombination {
  if (!freshColumns) {
    return combination;
  }
  const currentColumns: Record<string, SectionCell> = combination?.columns ?? {};
  const updates: Record<string, SectionCell> = {};
  calculationColumnIds.forEach((columnId) => {
    const freshCell = freshColumns[columnId];
    if (freshCell && !isSameCell(currentColumns[columnId], freshCell)) {
      updates[columnId] = freshCell;
    }
  });
  if (Object.keys(updates).length === 0) {
    return combination;
  }
  return { ...combination, columns: { ...currentColumns, ...updates } };
}

/**
 * Compares two calculation cells by the parts the grid renders.
 *
 * @param {SectionCell | undefined} current - The cell currently shown.
 * @param {SectionCell} fresh - The cell just computed by the server.
 * @returns {boolean} True when rendering would not change.
 */
function isSameCell(current: SectionCell | undefined, fresh: SectionCell): boolean {
  if (!current) {
    return false;
  }
  return current.value === fresh.value && JSON.stringify(current.columnDetails ?? null) === JSON.stringify(fresh.columnDetails ?? null);
}
